import { isEqual } from 'lodash';

import { EcomState, GProduct, UiState, WishListState } from '@/types';

import {
  initialEcomState,
  initialUiState,
  initiaWishListState,
} from './variables';
import {
  createPersistanceStore,
  createStore,
  PersistOptions,
} from './createStore';

const ecomStore = createPersistanceStore<EcomState>(
  initialEcomState,
  `@gapcommerce/${process?.env?.NEXT_PUBLIC_STORE_ID}`,
  process?.env?.NEXT_PUBLIC_STORAGE_VERSION as string,
);

const useEcomStore = (): [
  EcomState,
  (_arg: Partial<EcomState>) => void,
  () => void,
] => {
  const state = ecomStore<EcomState>(state => state, isEqual);

  const set = ecomStore.setState;

  const reset = () => set(initialEcomState);

  return [state as EcomState, set, reset];
};

const useEcomStoreSelector = <T extends keyof EcomState>(dependencies: T[]) => {
  const state: PickKeys<EcomState, T> = ecomStore(
    state => pickProperties(state, dependencies),
    isEqual,
  );

  const setState = ecomStore.setState;

  const reset = () => setState(initialEcomState);

  return { ...state, setState, reset };
};

/**
 * Normalizes fav_products from localStorage: old format stored full GProduct
 * objects; new format stores only product ids (strings). Keeps compatibility
 * with existing user data.
 */
function normalizeFavProducts(raw: unknown): WishListState['fav_products'] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .map((item: unknown): string => {
      if (typeof item === 'string') {
        return item;
      }
      if (item && typeof item === 'object' && 'id' in item) {
        const product = item as GProduct;
        return typeof product.id === 'string' ? product.id : '';
      }
      return '';
    })
    .filter(Boolean);
}

const wishListMerge: PersistOptions<WishListState>['merge'] = (
  persistedState,
  currentState,
) => {
  if (!persistedState || typeof persistedState !== 'object') {
    return currentState;
  }
  const raw = (persistedState as { fav_products?: (string | GProduct)[] })
    .fav_products;
  return {
    ...currentState,
    ...(persistedState as Partial<WishListState>),
    fav_products: normalizeFavProducts(raw),
  };
};

const wishListStore = createPersistanceStore(
  initiaWishListState,
  `@gapcommerce/wl/${process?.env?.NEXT_PUBLIC_STORE_ID}`,
  process?.env?.NEXT_PUBLIC_STORAGE_VERSION as string,
  { merge: wishListMerge },
);

const useWishListStore = (): [
  WishListState,
  // eslint-disable-next-line no-unused-vars
  (arg: Partial<WishListState>) => void,
] => {
  const state = wishListStore(state => state, isEqual);

  const set = wishListStore.setState;

  return [state, set];
};

const uiStore = createStore(initialUiState);

/** Global overlays/modals: only one may be open at a time. */
const UI_OVERLAY_KEYS = [
  'is_cart_open',
  'is_search_open',
  'is_store_location_open',
  'is_nav_open',
  'is_megamenu_open',
  'is_menu_user_open',
  'is_login_open',
  'is_regulation_modal_open',
  'is_special_bogo_modal_open',
  'is_store_validator_modal_open',
] as const;

type UiOverlayKey = typeof UI_OVERLAY_KEYS[number];

const closeUiOverlays = (): Pick<UiState, UiOverlayKey> =>
  Object.fromEntries(UI_OVERLAY_KEYS.map(key => [key, false])) as Pick<
    UiState,
    UiOverlayKey
  >;

const setUiState = (
  update: Partial<UiState> | ((_state: UiState) => Partial<UiState>),
) => {
  uiStore.setState(current => {
    const patch = typeof update === 'function' ? update(current) : update;
    const isOpeningOverlay = UI_OVERLAY_KEYS.some(key => patch[key] === true);

    if (!isOpeningOverlay) {
      return patch;
    }

    return { ...closeUiOverlays(), ...patch };
  });
};

const useUiStore = (): [
  UiState,
  (_arg: Partial<UiState>) => void,
  () => void,
] => {
  const state = uiStore(state => state, isEqual);

  const reset = () => setUiState(initialUiState);

  return [state, setUiState, reset];
};

const useUiStoreSelector = <T extends keyof UiState>(dependencies: T[]) => {
  const state: PickKeys<UiState, T> = uiStore(
    state => pickProperties(state, dependencies),
    isEqual,
  );

  const reset = () => setUiState(initialUiState);

  return { ...state, setState: setUiState, reset };
};

type PickKeys<T, K extends keyof T> = {
  [P in K]: T[P];
};

const pickProperties = <T extends {}, K extends keyof T>(
  obj: T,
  keys: K[],
): PickKeys<T, K> => {
  const pickedProperties = {} as PickKeys<T, K>;
  keys.forEach(key => {
    pickedProperties[key] = obj[key];
  });
  return pickedProperties;
};

export {
  useEcomStore,
  useWishListStore,
  useUiStore,
  useEcomStoreSelector,
  useUiStoreSelector,
};
